/* eslint-disable react/jsx-props-no-spreading */
import useRouter from 'AppShell/hooks/useRouter';
import { useLogger } from 'AppShell/logger/LoggerProvider';
import React, { FC, ReactElement, useEffect } from 'react';
import { Link, LinkProps, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { Dictionary } from 'Utils/types/utility';

export const StyledAnchor = styled.a`
    text-decoration: none;
`;

export const StyledLink = styled(Link)`
    text-decoration: none;
`;

export const StyledNavLink = styled(NavLink)`
    text-decoration: none;
`;

export interface Props extends LinkProps {
    to: string;
    className?: string;
    isCurrentPage?: boolean;
    target?: string;
    isNavLink?: boolean;
    activeClassName?: string;
    testId?: string;
    'data-testid'?: string;
    isExternalLink?: boolean;
    [key: `data-${string}`]: string | undefined;
}

const AppShellLink: React.FC<Props> = ({
    children,
    to,
    className,
    isNavLink,
    activeClassName,
    innerRef,
    ...otherAttributesAndHandlers
}) =>
    isNavLink ? (
        <StyledNavLink
            to={to}
            activeClassName={activeClassName}
            className={className}
            innerRef={innerRef}
            {...otherAttributesAndHandlers}
        >
            {children}
        </StyledNavLink>
    ) : (
        <StyledLink to={to} className={className} innerRef={innerRef} {...otherAttributesAndHandlers}>
            {children}
        </StyledLink>
    );

const buildAdditionalAttributes = ({
    isCurrentPage,
    'aria-current': ariaCurrent,
    target,
    'data-testid': dataTestId,
    testId,
    ...restAttributes
}: Pick<Props, 'isCurrentPage' | 'target' | 'testId' | 'data-testid' | 'aria-current'>): Dictionary<string> => {
    const additionalAttributes: Dictionary<string> = {};
    const notEmptyTestId = dataTestId || testId;

    if (isCurrentPage || ariaCurrent) {
        additionalAttributes['aria-current'] = ariaCurrent?.toString() || 'page';
    }

    if (target) {
        additionalAttributes.target = target;
        if (target === '_blank') {
            additionalAttributes.rel = 'noopener noreferrer';
        }
    }

    if (notEmptyTestId) {
        additionalAttributes['data-testid'] = notEmptyTestId;
    }

    (Object.entries(restAttributes) as Array<[string, string]>).forEach(([key, value]) => {
        if (key.startsWith('data-')) {
            additionalAttributes[key] = value;
        }
    });

    return additionalAttributes;
};

const HybridLink: FC<Props> = ({
    to,
    children,
    className,
    onClick: onClickFunction,
    isCurrentPage = false,
    onMouseEnter: onMouseEnterFunction,
    innerRef,
    target,
    isNavLink,
    activeClassName,
    testId,
    'data-testid': dataTestId,
    'aria-current': ariaCurrent,
    isExternalLink = false,
    title,
    ...rest
}): ReactElement => {
    const additionalAttributes = buildAdditionalAttributes({
        isCurrentPage,
        'aria-current': ariaCurrent,
        target,
        testId,
        'data-testid': dataTestId,
        ...rest,
    });

    const logger = useLogger();
    const { disableClientsideNavigation } = useRouter();

    const eventHandlers = { onClick: onClickFunction, onMouseEnter: onMouseEnterFunction };
    const additionalProps = { ...eventHandlers, ...additionalAttributes };

    const href = (to || '').toString();

    useEffect(() => {
        if (!href.length) {
            logger.warn('HybridLink: props.to is empty!', new Error('HybridLink: props.to is empty!'));
        }
    }, [href, logger]);

    if (isExternalLink || !!disableClientsideNavigation) {
        return (
            <StyledAnchor title={title} href={href} className={className} {...additionalProps}>
                {children}
            </StyledAnchor>
        );
    }

    return (
        <AppShellLink
            title={title}
            isNavLink={isNavLink}
            activeClassName={activeClassName}
            to={to}
            className={className}
            innerRef={innerRef}
            {...additionalProps}
        >
            {children}
        </AppShellLink>
    );
};

export default HybridLink;
